import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import "rc-time-picker/assets/index.css";
import { Modal, ModalBody, Spinner } from "reactstrap";
import crossIcon from "assets/images/cross.svg";
import Text from "components/Text";
import styles from "../OfficePolicyManagement.module.scss";
import Select from "react-select";
import { useAllActiveOffices } from "repositories/scheduler-repository";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import constants, {
  officePolicyAssignmentStatus,
  officePolicyStatusOptions,
  policyFolderSortOptions,
} from "../../../../constants";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import usePolicyModuleListing from "../hooks/usePolicyModuleListing";
import { sortingVariablesForCurrentPolicies } from "../tabs/constants";
import { getPolicyStatusClass } from "./PolicyDocumentListing";
import Empty from "components/Empty";
function SelectPolicyModal({
  t,
  issaveEmployeeModalOpen,
  setIsSaveEmployeeModalOpen,
  policiesIds,
  setPoliciesIds,
}) {
  const {
    officeDataApi,
    setIsRenew,
    handleSearchTerm,
    categoryList,
    setSortBy,
    appliedDateRange,
    setAppliedDateRange,
    setAppliedOfficeFilters,
    setAppliedStatusFilters,
    setAppliedCategoryFilters,
    setDueDateFilter,
    setIsDescending,
    officePolicyLoading,
    officePolicyFetching,
  } = usePolicyModuleListing(false, true);
  const { effectiveOwnerId } = useAccountOwnerCommon();
  const [officePoliciesData, setOfficePoliciesData] = useState([]);
  const { defaultOfficeValue, defaultCategoryValue, defaultStatuses } = useMemo(
    () => ({
      defaultOfficeValue: {
        value: 0,
        label: t("accountOwner.allOffices"),
      },
      defaultCategoryValue: {
        value: 0,
        label: t("officePolicyManagement.allCategories"),
      },
      defaultStatuses: {
        value: "",
        label: t("officePolicyManagement.allPolicies"),
      },
    }),
    [t],
  );

  useEffect(() => {
    setAppliedDateRange({ fromDate: null, toDate: null });
    setAppliedOfficeFilters([]);
    setAppliedStatusFilters([]);
    setAppliedCategoryFilters([]);
    setSortBy(sortingVariablesForCurrentPolicies.Category);
    setIsDescending(true);
    setDueDateFilter(policyFolderSortOptions[0].id);
    setIsRenew(true);
  }, []);

  const [selectionStatuses, setSelectionStatuses] = useState(defaultStatuses);
  const [selectionCategory, setSelectionCategory] =
    useState(defaultCategoryValue);
  const [selectionOffice, setSelectionOffice] = useState(defaultOfficeValue);
  const [search, setSearch] = useState("");
  const [selectedPolicies, setSelectedPolicies] = useState(policiesIds);

  useEffect(() => {
    if (officeDataApi?.items?.length) {
      setOfficePoliciesData(officeDataApi.items);
    } else {
      setOfficePoliciesData([]);
    }
  }, [officeDataApi?.items]);
  const { isLoading: officeDataLoading, data: officeData } =
    useAllActiveOffices(1, constants.ACTIVE_OFFICES_PAGE_SIZE, {
      ownerId: effectiveOwnerId,
      includeAdminOffice: true,
    });

  const officeList = useMemo(() => {
    if (!officeDataLoading && officeData?.data?.length) {
      const data = officeData?.data?.map((res) => ({
        value: res.id,
        label: res.name,
      }));
      data?.unshift({
        value: defaultOfficeValue.value,
        label: defaultOfficeValue.label,
      });
      return data;
    }
    return [];
  }, [officeDataLoading, officeData?.data, defaultOfficeValue]);

  const categoryListData = useMemo(() => {
    const data = (Array.isArray(categoryList) ? categoryList : []).map(
      (res) => ({
        value: res.id,
        label: res.title,
      }),
    );

    data.unshift({
      value: defaultCategoryValue.value,
      label: defaultCategoryValue.label,
    });
    return data;
  }, [categoryList?.data, categoryList]);
  const statusList = useMemo(() => {
    const data = officePolicyStatusOptions.map((res) => ({
      value: res.value,
      label: res.label,
    }));
    return data;
  }, [defaultStatuses]);

  const onSearch = (val) => {
    setSearch(val);
  };

  const checkMember = (e, member) => {
    if (e.target.checked) {
      setSelectedPolicies([...selectedPolicies, member]);
    } else {
      setSelectedPolicies(
        selectedPolicies.filter((res) => res.id !== member.id),
      );
    }
  };

  const checkMemberRoleAvaliable = (array, value) => {
    return array.some((e) => e.id === value.id);
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked && officePoliciesData.length > 0) {
      setSelectedPolicies(officePoliciesData);
    } else {
      setSelectedPolicies([]);
    }
  };
  const getPolicyStatus = (policy) => {
    const statuses = {
      [officePolicyAssignmentStatus.IN_PROGRESS]: "In Progress",
      [officePolicyAssignmentStatus.PENDING]: "Pending",
      [officePolicyAssignmentStatus.ACCEPTED]: "Accepted",
      [officePolicyAssignmentStatus.STARTED]: "Just Started",
      [officePolicyAssignmentStatus.COMPLETED]: "Completed",
      [officePolicyAssignmentStatus.REJECTED]: "Rejected",
      [officePolicyAssignmentStatus.CANCELLED]: "Cancelled",
      [officePolicyAssignmentStatus.DRAFT]: "Draft",
    };
    if (
      policy?.assignees?.length === 0 &&
      policy?.policyStatus === officePolicyAssignmentStatus.PENDING
    ) {
      return t("officePolicyManagement.pendingNotAssigned");
    }
    return statuses[policy?.policyStatus] || "—";
  };
  const isLoadingData = officePolicyFetching || officePolicyLoading;
  return (
    <Modal
      isOpen={issaveEmployeeModalOpen}
      toggle={() => setIsSaveEmployeeModalOpen(false)}
      className="modal-dialog-centered modal-lg"
      modalClassName="custom-modal"
    >
      <span
        className="close-btn"
        onClick={() => setIsSaveEmployeeModalOpen(false)}
      >
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody className={styles["add-employee-modal-dialog"]}>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          <span className="modal-title-25">
            {t("officePolicyManagement.selectPolicy")}
          </span>
        </Text>
        <div className={"search-box w-100 " + styles["search-box"]}>
          <input
            type="text"
            placeholder={t("officePolicyManagement.searchByName")}
            value={search}
            onChange={(e) => {
              onSearch(e.currentTarget.value);
              handleSearchTerm(e);
            }}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>

        <div className={styles.employeeSelectionSelectBox}>
          <Select
            className="react-select-container"
            classNamePrefix="react-select"
            options={officeList}
            value={selectionOffice}
            onChange={(val) => {
              setSelectionOffice(val);
              setAppliedOfficeFilters(val ? [val.value] : []);
            }}
            isSearchable={false}
          />
          <Select
            className="react-select-container"
            classNamePrefix="react-select"
            options={categoryListData}
            value={selectionCategory}
            onChange={(val) => {
              setSelectionCategory(val);
              setAppliedCategoryFilters(val ? [val.value] : []);
            }}
            isSearchable={false}
          />
          <Select
            className="react-select-container"
            classNamePrefix="react-select"
            options={statusList}
            value={selectionStatuses}
            onChange={(val) => {
              setSelectionStatuses(val);
              setAppliedStatusFilters(val ? [val.value] : []);
            }}
            isSearchable={false}
          />
        </div>
        <div
          className={` d-flex gap-xs c-field ${styles["policy-modal-datepicker"]} `}
        >
          <div className="d-flex inputdate">
            <ReactDatePickerCommon
              className="c-form-control"
              selected={appliedDateRange?.fromDate}
              onChange={(e) => {
                setAppliedDateRange((prev) => {
                  return {
                    ...prev,
                    fromDate: e ? e : null,
                  };
                });
              }}
              minDate={new Date()}
              placeholderText="Start Date"
            />
          </div>
          <div className="d-flex inputdate">
            <ReactDatePickerCommon
              className="c-form-control"
              selected={appliedDateRange?.toDate}
              onChange={(e) => {
                setAppliedDateRange((prev) => {
                  return {
                    ...prev,
                    toDate: e ? e : null,
                  };
                });
              }}
              minDate={new Date()}
              placeholderText="Due Date"
            />
          </div>
        </div>
        {officePoliciesData.length > 0 && (
          <>
            <Text className={` ${styles["policy-modal-text"]} `}>
              {selectedPolicies?.length} {t("accountOwner.selected")}
            </Text>

            <div className="ch-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={
                    selectedPolicies.length > 0 &&
                    officePoliciesData.every((policy) =>
                      selectedPolicies.some(
                        (selected) => selected.id === policy.id,
                      ),
                    )
                  }
                  onChange={toggleSelectAll}
                />
                <span> {t("staffTimesheet.selectAll")}</span>
              </label>
            </div>
          </>
        )}

        <ul className={"modal-employee-list " + styles["employee-list-box"]}>
          {officePoliciesData.length > 0 &&
            officePoliciesData.map((val) => {
              return (
                <li key={val.id}>
                  <div className="ch-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={checkMemberRoleAvaliable(
                          selectedPolicies,
                          val,
                        )}
                        onChange={(e) => checkMember(e, val)}
                      />
                      <span className={` ${styles["employee-list-wrapper"]} `}>
                        <div
                          className={` ${styles["employee-list-item-box"]} `}
                        >
                          <div className={` ${styles["title_text"]} `}>
                            {val.title}
                          </div>
                          <div
                            className={` ${styles["category_text"]} `}
                            style={
                              val.categoryName ? {} : { minHeight: "21px" }
                            }
                          >
                            {val.categoryName}
                          </div>
                        </div>
                        <div
                          className={` ${getPolicyStatusClass(val?.policyStatus) || styles["progress-chips"]} ${styles["policy-status-chip"]} `}
                        >
                          {getPolicyStatus(val)}
                        </div>
                      </span>
                    </label>
                  </div>
                </li>
              );
            })}
          {!isLoadingData && officePoliciesData.length === 0 && (
            <li>
              <Empty
                Message={t(
                  "officePolicyManagement.noPolicyAvailableForSelection",
                )}
              />
            </li>
          )}
          {isLoadingData && (
            <li>
              <div style={{ padding: "1rem", textAlign: "center" }}>
                <Spinner
                  animation="border"
                  size="sm"
                  className="loader-spinner"
                />
              </div>
            </li>
          )}
        </ul>
        <button
          className="button button-round button-shadow mr-sm-3  mb-sm-3 mb-md-0 mt-sm-0 mt-md-3 w-sm-100"
          title={t("accountOwner.addEmployees")}
          onClick={() => {
            setPoliciesIds(selectedPolicies);
            setIsSaveEmployeeModalOpen(false);
          }}
        >
          {t("officePolicyManagement.select")}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark  mb-sm-3 mb-md-0 mt-sm-0 mt-md-3"
          onClick={() => setIsSaveEmployeeModalOpen(false)}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(SelectPolicyModal);
